'use client';

import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Icon } from '@ironhack/design-system2/components/icon';
import { HStack } from '@ironhack/design-system2/components/stack';
import { Text } from '@ironhack/design-system2/components/text';
import { Facebook } from '@ironhack/design-system2/icons/facebook';
import { Instagram } from '@ironhack/design-system2/icons/instagram';
import { Twitter } from '@ironhack/design-system2/icons/twitter';
import { Youtube } from '@ironhack/design-system2/icons/youtube';
import { type ReactElement } from 'react';

import { Button } from '@/app/components/client/button';
import { Link } from '@/app/components/client/link';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { type FullParams, type PageType } from '@/app/urls/url-mapping';
import { type RegionLanguageMap } from '@/types';
import { type FooterData } from './footer';
import { FooterColumn } from './footer-column';
import { RegionSelect } from './region-select';

type FooterClientProps = {
  data: FooterData;
  page: PageType;
  params: FullParams;
  regionOptions?: Partial<RegionLanguageMap>;
} & FlexProps;

const socialIconMap = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  twitter: Twitter,
};

export const FooterClient = (props: FooterClientProps): ReactElement => {
  const { data, page, params, regionOptions, ...flexProps } = props;

  const {
    button: [button],
    copyright,
    firstColumnLinks,
    firstColumnTitle,
    privacyPolicy: [privacyPolicy],
    region: dataRegion,
    regions,
    secondColumnLinks,
    secondColumnTitle,
    selectorTitle,
    termsOfUse: [termsOfUse],
    thirdColumnLinks,
    thirdColumnTitle,
  } = data;
  const socialLinks = ['instagram', 'facebook', 'youtube', 'twitter'] as const;

  return (
    <ModuleWrapper
      innerProps={{ gap: 6, color: 'text.inverse', direction: 'column' }}
      moduleFormat={{
        background: 'dark',
        spacingTop: '48',
        spacingBottom: '48',
      }}
      outerProps={{ direction: 'column', as: 'footer', ...flexProps }}
    >
      <Flex
        direction={['column', null, 'row']}
        gap={[5, null, 3]}
        justifyContent="space-between"
        wrap="wrap"
      >
        <FooterColumn links={firstColumnLinks} title={firstColumnTitle} />
        <FooterColumn
          links={secondColumnLinks}
          noFollow
          title={secondColumnTitle}
        />
        <FooterColumn
          links={thirdColumnLinks}
          noFollow
          title={thirdColumnTitle}
        />
        <Flex alignItems="center" direction="column">
          <Text alignSelf="start" mb={2} textStyle="2xl">
            {selectorTitle}
          </Text>
          <RegionSelect
            page={page}
            params={params}
            region={dataRegion}
            regionOptions={regionOptions}
            regions={regions}
          />
          <Button tracking={{ f: 'fb' }} w="full" {...button} rel="nofollow" />
          <HStack mt={5} spacing={3}>
            {socialLinks.map((socialLink) => (
              <a href={data[socialLink]} key={socialLink}>
                <Icon
                  as={socialIconMap[socialLink]}
                  boxSize="24px"
                  color="text.inverse"
                />
              </a>
            ))}
          </HStack>
        </Flex>
      </Flex>
      <Flex
        alignItems="center"
        direction={['column', null, 'row']}
        gap={2}
        justify={['center', null, 'space-between']}
        w="full"
      >
        <Flex gap={2}>
          <Link
            _hover={{
              color: 'link.inverse',
              textDecoration: 'underline',
            }}
            color="link.inverse"
            {...privacyPolicy.target[0]}
          >
            {privacyPolicy.text}
          </Link>
          <Link
            _hover={{
              color: 'link.inverse',
              textDecoration: 'underline',
            }}
            color="text.inverse"
            {...termsOfUse.target[0]}
          >
            {termsOfUse.text}
          </Link>
        </Flex>
        <Text align={['center', null, 'start']}>{copyright}</Text>
      </Flex>
    </ModuleWrapper>
  );
};
